import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	List,
	ListItemButton,
	ListItemAvatar,
	Avatar,
	ListItemText,
	TextField,
	InputAdornment,
	CircularProgress,
	Box,
	Typography,
	IconButton,
	Skeleton,
	ListItem,
	Checkbox,
	Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { useAuth } from "@/hooks/auth";

interface User {
	id: string;
	firstName: string;
	lastName: string;
	avatar: string;
	email: string;
	isOnline?: boolean;
	username: string;
}

interface CreateGroupDialogProps {
	open: boolean;
	onClose: () => void;
	onCreateGroup: (name: string, description: string, participantIds: string[]) => void;
}

export const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({
	open,
	onClose,
	onCreateGroup,
}) => {
	const theme = useTheme();
	const { user: currentUser } = useAuth();
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	
	const [groupName, setGroupName] = useState("");
	const [description, setDescription] = useState("");
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!open) {
			// Reset on close
			setGroupName("");
			setDescription("");
			setSearch("");
			setSelectedUserIds([]);
			return;
		}

		const fetchUsers = async () => {
			setLoading(true);
			try {
				const res = await fetch(
					`${process.env.REACT_APP_API_URL}/users?limit=50&search=${encodeURIComponent(search)}`,
					{
						headers: {
							Authorization: `Bearer ${localStorage.getItem("token")}`,
						},
					},
				);
				const data = await res.json();
				const fetchedUsers = data?.data?.users || data?.users || [];
				setUsers(fetchedUsers.filter((u: User) => u.id !== currentUser?.id));
			} catch (err) {
				console.error("Failed to fetch users", err);
			} finally {
				setLoading(false);
			}
		};

		const timer = setTimeout(fetchUsers, 300); // debounce search
		return () => clearTimeout(timer);
	}, [open, search, currentUser?.id]);

	const handleToggleUser = (userId: string) => {
		setSelectedUserIds((prev) =>
			prev.includes(userId)
				? prev.filter((id) => id !== userId)
				: [...prev, userId]
		);
	};

	const handleSubmit = async () => {
		if (!groupName.trim() || selectedUserIds.length === 0) return;
		setIsSubmitting(true);
		try {
			await onCreateGroup(groupName.trim(), description.trim(), selectedUserIds);
			onClose();
		} catch (err) {
			console.error("Failed to create group", err);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog 
			open={open} 
			onClose={onClose} 
			fullWidth 
			maxWidth="xs"
			PaperProps={{
				sx: {
					borderRadius: 3,
					backgroundImage: "none",
					backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.95)',
					backdropFilter: "blur(16px)",
					boxShadow: theme.shadows[16],
				}
			}}
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<GroupAddIcon color="primary" />
					Групп чат үүсгэх
				</Typography>
				<IconButton size="small" onClick={onClose} sx={{ color: "text.secondary", backgroundColor: "action.hover" }}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{ p: 0, display: "flex", flexDirection: "column", height: 500 }}
			>
				<Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
					<TextField
						fullWidth
						size="small"
						label="Группын нэр"
						placeholder="Группын нэрийг оруулна уу"
						value={groupName}
						onChange={(e) => setGroupName(e.target.value)}
						required
					/>
					
					<TextField
						fullWidth
						size="small"
						placeholder="Гишүүн хайх..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" color="action" />
								</InputAdornment>
							),
							sx: { 
								borderRadius: 2, 
								backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
								"& fieldset": { borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }
							}
						}}
					/>
				</Box>

				<Typography variant="caption" sx={{ px: 2, pb: 1, color: "text.secondary", fontWeight: "bold" }}>
					ГИШҮҮД СОНГОХ ({selectedUserIds.length})
				</Typography>

				<List 
					sx={{ 
						flexGrow: 1, 
						overflowY: "auto",
						"&::-webkit-scrollbar": { width: 6 },
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
							borderRadius: 3,
							"&:hover": {
								backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
							},
						},
						px: 1,
					}}
				>
					{loading && users.length === 0 ? (
						Array.from({ length: 5 }).map((_, idx) => (
							<ListItem key={idx} disablePadding dense sx={{ mb: 0.5 }}>
								<ListItemButton sx={{ borderRadius: 2 }}>
									<Checkbox disabled />
									<ListItemAvatar>
										<Skeleton variant="circular" width={40} height={40} />
									</ListItemAvatar>
									<ListItemText
										primary={<Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />}
										secondary={<Skeleton variant="text" width="40%" />}
									/>
								</ListItemButton>
							</ListItem>
						))
					) : users.length === 0 ? (
						<Box sx={{ p: 4, textAlign: "center", color: "text.secondary", mt: 2 }}>
							<SearchIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
							<Typography variant="body2">Илэрц олдсонгүй</Typography>
						</Box>
					) : (
						users.map((u) => {
							const isSelected = selectedUserIds.includes(u.id);
							return (
								<ListItem key={u.id} disablePadding dense sx={{ mb: 0.5 }}>
									<ListItemButton
										onClick={() => handleToggleUser(u.id)}
										sx={{ 
											borderRadius: 2,
											backgroundColor: isSelected 
												? theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.15)" : "rgba(25, 118, 210, 0.08)"
												: "transparent"
										}}
									>
										<Checkbox 
											edge="start" 
											checked={isSelected}
											tabIndex={-1}
											disableRipple
										/>
										<ListItemAvatar>
											<Avatar src={u.avatar} alt={u.firstName} sx={{ width: 40, height: 40 }}>
												{u.firstName?.[0] || u.username?.[0] || "?"}
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={
												<Typography variant="subtitle2" color="text.primary" fontWeight="600" noWrap>
													{[u.firstName, u.lastName]
														.filter((n) => n && n !== "undefined")
														.join(" ") ||
													(u.username !== "undefined" ? u.username : "") ||
													u.email ||
													"Unknown User"}
												</Typography>
											}
											secondary={
												<Box
													component="span"
													sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
												>
													<Box
														sx={{
															width: 8,
															height: 8,
															borderRadius: "50%",
															backgroundColor: u.isOnline
																? "success.main"
																: "text.disabled",
														}}
													/>
													<Typography variant="caption" color="text.secondary">
														{u.isOnline ? "Онлайн" : "Офлайн"}
													</Typography>
												</Box>
											}
										/>
									</ListItemButton>
								</ListItem>
							);
						})
					)}
				</List>
			</DialogContent>
			<DialogActions sx={{ p: 2, pt: 1, borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}` }}>
				<Button onClick={onClose} color="inherit" disabled={isSubmitting}>
					Цуцлах
				</Button>
				<Button 
					onClick={handleSubmit} 
					variant="contained" 
					disabled={!groupName.trim() || selectedUserIds.length === 0 || isSubmitting}
					startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <GroupAddIcon />}
				>
					Үүсгэх
				</Button>
			</DialogActions>
		</Dialog>
	);
};
